import React, { useEffect, useState } from 'react'
import { Box, Button, CircularProgress, MenuItem, Paper, TextField, Typography, makeStyles } from '@material-ui/core'
import { Redirect } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { userProfile } from '@views/profile/state/profileSlice'
import { DailyChart, DistributionChart, MonthlyAverageChart } from './components/Charts'
import { loadAgentMetrics, selectAgentMetrics } from './state/agentMetricsSlice'
import { bogotaPeriod, canViewAgentMetrics } from './utils'

const useStyles = makeStyles(theme => ({
  root: {
    maxWidth: 1500,
    margin: '84px auto 0',
    padding: theme.spacing(3),
    minHeight: 'calc(100vh - 84px)',
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(2),
      marginTop: 72
    }
  },
  heading: { fontWeight: 700 },
  filters: {
    display: 'flex',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
    margin: theme.spacing(2, 0)
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: theme.spacing(2),
    [theme.breakpoints.down('sm')]: { gridTemplateColumns: 'minmax(0, 1fr)' }
  },
  status: { padding: theme.spacing(6), textAlign: 'center' },
  live: { color: '#66707a' }
}))

const AgentMetricsView: React.FC = () => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const profile = useSelector(userProfile)
  const state = useSelector(selectAgentMetrics)
  const initialPeriod = bogotaPeriod()
  const [period, setPeriod] = useState(initialPeriod)
  const [year, setYear] = useState(Number(initialPeriod.slice(0, 4)))
  const allowed = canViewAgentMetrics(profile.role)

  useEffect(() => {
    if (allowed) dispatch(loadAgentMetrics({ period, year }))
  }, [allowed, dispatch, period, year])

  useEffect(() => {
    if (!state.data) return
    if (period < state.data.range.minimumPeriod) setPeriod(state.data.range.minimumPeriod)
    if (period > state.data.range.maximumPeriod) setPeriod(state.data.range.maximumPeriod)
  }, [period, state.data])

  if (!allowed) return <Redirect to="/" />
  const years = state.data
    ? Array.from(
        {
          length: state.data.range.maximumYear - state.data.range.minimumYear + 1
        },
        (_, index) => state.data!.range.minimumYear + index
      )
    : [year]
  const retry = () => dispatch(loadAgentMetrics({ period, year }))

  return (
    <Box className={classes.root}>
      <Typography className={classes.heading} variant="h4">
        Métricas
      </Typography>
      <Typography className={classes.live} variant="body2">
        Datos live de tus casos ahorradores registrados.
      </Typography>
      <Box className={classes.filters} aria-label="Filtros de métricas">
        <TextField
          label="Mes"
          type="month"
          value={period}
          inputProps={{
            min: state.data?.range.minimumPeriod,
            max: state.data?.range.maximumPeriod
          }}
          onChange={event => {
            const value = event.target.value
            setPeriod(value)
            setYear(Number(value.slice(0, 4)))
          }}
          InputLabelProps={{ shrink: true }}
        />
        <TextField select label="Año" value={year} onChange={event => setYear(Number(event.target.value))}>
          {years.map(value => (
            <MenuItem key={value} value={value}>
              {value}
            </MenuItem>
          ))}
        </TextField>
      </Box>
      {state.loading && !state.data ? (
        <Paper className={classes.status}>
          <CircularProgress aria-label="Cargando métricas" />
        </Paper>
      ) : state.error ? (
        <Paper className={classes.status}>
          <Typography gutterBottom>No fue posible cargar las métricas.</Typography>
          <Button color="primary" variant="contained" onClick={retry}>
            Reintentar
          </Button>
        </Paper>
      ) : state.data ? (
        <>
          {state.data.selectedMonthTotal === 0 && (
            <Paper className={classes.status}>No hay casos registrados en el mes seleccionado.</Paper>
          )}
          <Box className={classes.grid}>
            <DailyChart data={state.data.dailyRegistrations} />
            <MonthlyAverageChart data={state.data.monthlyAverages} />
            <DistributionChart
              title="Casos por banco"
              description="Distribución de los casos del mes según banco."
              data={state.data.banks}
            />
            <DistributionChart
              title="Casos por ciudad"
              description="Top 10 de ciudades normalizadas; las restantes se agrupan como OTRAS."
              data={state.data.cities}
            />
          </Box>
        </>
      ) : null}
    </Box>
  )
}

export default AgentMetricsView
